/**
 * CustomerCreate — register a single customer.
 * Route: /customers/new
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppButton, AppCard, AppField, AppFormAlert, AppInput } from "../../../shared/components/ui";
import { IconSpinner } from "../../../shared/components/icons";
import { PageHeader } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";
import { isValidTzMobile, normalizeTzPhone } from "../../../shared/phone";

type Form = {
  first_name: string;
  last_name: string;
  phone: string;
  national_id: string;
  ward: string;
  village: string;
  district: string;
  region: string;
};

const EMPTY: Form = {
  first_name: "",
  last_name: "",
  phone: "+255",
  national_id: "",
  ward: "",
  village: "",
  district: "",
  region: "",
};

export function CustomerCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const phone = normalizeTzPhone(form.phone);
    if (!isValidTzMobile(phone)) {
      setError("Enter a valid Tanzania mobile number, for example +255712345678.");
      return;
    }
    setBusy(true);
    try {
      const body: Record<string, string> = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone,
      };
      for (const key of ["national_id", "ward", "village", "district", "region"] as const) {
        if (form[key].trim()) body[key] = form[key].trim();
      }
      const res = await api.post<{ customer: { id: number } }>("/customers", body);
      navigate(`/customers/${res.customer.id}`);
    } catch (ex: unknown) {
      setError(ex instanceof Error ? ex.message : "Create failed");
      setBusy(false);
    }
  };

  return (
    <div>
      <AppFormAlert
        open={!!error}
        title="Customer could not be created"
        message={error}
        onClose={() => setError("")}
      />
      <PageHeader
        title="Register customer"
        subtitle="Register one household. For many rows, use bulk import."
        crumbs={[
          { label: "Customers", to: "/customers" },
          { label: "New" },
        ]}
      />

      <AppCard>
        <form onSubmit={submit}>
          <div style={{ display: "grid", gap: "1rem", maxWidth: 520 }}>
            <AppField label="First name">
              <AppInput value={form.first_name} onChange={set("first_name")} required disabled={busy} />
            </AppField>
            <AppField label="Last name">
              <AppInput value={form.last_name} onChange={set("last_name")} required disabled={busy} />
            </AppField>
            <AppField label="Phone (+255…)">
              <AppInput
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: normalizeTzPhone(e.target.value) }))}
                onBlur={() => setForm((f) => ({ ...f, phone: normalizeTzPhone(f.phone) }))}
                required
                disabled={busy}
                placeholder="+255712345678"
              />
            </AppField>
            <AppField label="National ID (optional)">
              <AppInput value={form.national_id} onChange={set("national_id")} disabled={busy} />
            </AppField>
            <AppField label="Ward">
              <AppInput value={form.ward} onChange={set("ward")} disabled={busy} />
            </AppField>
            <AppField label="Village">
              <AppInput value={form.village} onChange={set("village")} disabled={busy} />
            </AppField>
            <AppField label="District">
              <AppInput value={form.district} onChange={set("district")} disabled={busy} />
            </AppField>
            <AppField label="Region">
              <AppInput value={form.region} onChange={set("region")} disabled={busy} />
            </AppField>
            <div style={{ display: "flex", gap: 8 }}>
              <AppButton type="submit" disabled={busy}>
                {busy ? <><IconSpinner size={14} /> Saving…</> : "Create customer"}
              </AppButton>
              <Link to="/customers">
                <AppButton type="button" variant="secondary" disabled={busy}>Cancel</AppButton>
              </Link>
            </div>
          </div>
        </form>
      </AppCard>
    </div>
  );
}
